import {queryClient} from '@/lib/queryClient'
import {userService} from '@/services/userService'
import {storage, STORAGE_KEYS} from '@/utils/storage'

/**
 * The booking identity gate, in one place.
 *
 * The backend refuses POST /trips and POST /trips/{trip}/confirm with
 * `422 identity_incomplete` until the account has a real name, a real email AND a phone.
 * Name and email were already enforced for guest quote passengers; THE PHONE IS THE NEW
 * RULE, and the one that will actually bite — Google and Apple never supply one, and a
 * desk-created guest has a placeholder identity until they claim the booking.
 *
 * Everything here reads the SERVER'S answer rather than inferring one. `requires_identity`
 * and `identity_missing` ride on every user payload for exactly this purpose, and a
 * refused booking repeats the list under `data.missing`. Guessing from the sign-in method
 * ("signed in by phone, so they have a phone") looks equivalent and is not: the number
 * typed at an OTP login is not necessarily stored against the account, and it says nothing
 * about the name or email the gate also checks.
 */

export const IDENTITY_INCOMPLETE = 'identity_incomplete';

// Where a passenger goes to fix it. EditProfile already owns name, phone and email
// (with their verify flows), so the gate sends them there rather than to a second screen
// that would collect the same fields under a different heading.
export const IDENTITY_PROFILE_PATH = '/settings';

/**
 * The fields this account still owes, as a lowercase array ('name' | 'email' | 'phone').
 *
 * The boolean with no list means "something is missing but the server didn't say what" —
 * read as the phone, since that is the field the gate was added for and the only one a
 * social sign-up or a desk-created guest can never already have.
 */
export function identityMissing(user) {
    const missing = user?.identity_missing;
    if (Array.isArray(missing) && missing.length) return missing.map((field) => String(field).toLowerCase());
    return user?.requires_identity ? ['phone'] : [];
}

/** The same list, read off a REFUSED booking (`data.missing`) instead of a user. */
export function identityMissingFromError(result) {
    const missing = result?.raw?.data?.missing;
    if (Array.isArray(missing) && missing.length) return missing.map((field) => String(field).toLowerCase());
    return ['phone'];
}

/** Was this booking refused BECAUSE of the gate? */
export function isIdentityIncompleteError(result) {
    return result?.raw?.key === IDENTITY_INCOMPLETE;
}

/**
 * Where to send a passenger who has just signed in — the profile page when the account
 * owes the gate a field, otherwise wherever they were going.
 *
 * Called at EVERY point a token is set (OTP verify, Google, Apple, the phone-desk claim)
 * so the answer cannot depend on how they got in. That matters here more than anywhere:
 * the gate exists because Google and Apple never supply a phone, so the social paths are
 * precisely the ones that must not skip it.
 *
 * ⚠️ THIS DISCARDS THE PENDING REDIRECT, and that is the intended trade. A passenger who
 * followed a link to book and signed in would otherwise land on the booking and be
 * refused there by the same rule — the gate blocks the destination, so arriving at it
 * only to bounce is strictly worse than being told why up front. The profile page states
 * the reason on arrival (see EditProfile), so the diversion is never unexplained.
 */
export function postLoginPath(user, intended) {
    return identityMissing(user).length ? IDENTITY_PROFILE_PATH : (intended || '/');
}

/**
 * "phone number", "email address and phone number" — the sentence names what is actually
 * being asked for, so the passenger knows what to go and fix rather than being told their
 * profile is vaguely incomplete.
 */
export function identityMissingLabel(missing) {
    const labels = (Array.isArray(missing) ? missing : []).map((field) => {
        if (field === 'name') return 'name';
        if (field === 'email') return 'email address';
        return 'phone number';
    });
    if (!labels.length) return 'phone number';
    if (labels.length === 1) return labels[0];
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * Booking-modal version of the gate, for an ALREADY signed-in passenger: reads the
 * account and, when it still owes a field, returns the completeProfile step (with the
 * missing list + onward step stashed in storage for that screen). `null` = carry on.
 *
 * A failed account fetch returns `null` rather than blocking checkout — the refused
 * booking in processingBooking still routes to completeProfile as the safety net.
 */
export async function bookingIdentityStep(nextStep = 'paymentCards') {
    let user;
    try {
        user = await queryClient.fetchQuery({
            queryKey: ['user', 'account'],
            queryFn: async () => {
                const result = await userService.getAccount();
                if (!result.success) throw new Error(result.error);
                return result.data;
            },
            // Always ask the server — a cached account from before the phone was
            // removed/added would give the wrong answer at the exact moment it matters.
            staleTime: 0,
        });
    } catch {
        return null;
    }
    const missing = identityMissing(user);
    if (!missing.length) return null;
    return stashIdentityGate(missing, nextStep, user);
}

/** Store what completeProfile needs (it has no per-step props channel) and return its step. */
export function stashIdentityGate(missing, nextStep, user) {
    if (user?.first_name) storage.set(STORAGE_KEYS.FIRST_NAME, user.first_name);
    if (user?.last_name) storage.set(STORAGE_KEYS.LAST_NAME, user.last_name);
    storage.set(STORAGE_KEYS.IDENTITY_MISSING, missing);
    storage.set(STORAGE_KEYS.IDENTITY_NEXT_STEP, nextStep);
    return 'completeProfile';
}
